/**
 * `VaultClient` — one typed method per endpoint in the parent spec's API surface.
 *
 * Responsibilities: attach the heivol-id JWT, attach the umk-proof headers on
 * `jwt+umk-proof` endpoints, serialise/validate JSON. It performs NO vault crypto
 * — the caller (portal / extension) encrypts with `@heivol-locker/crypto` and
 * hands this client opaque blobs. The only key it touches is the unlock session's
 * UMK, used solely to MAC the umk-proof (never transmitted).
 */
import { z } from 'zod';
import { ApiError } from './transport.js';
import { umkProofHeaders } from './umk-proof.js';
import { uuidv4 } from './base64.js';
import * as S from './schemas.js';
export class VaultClient {
    opts;
    session = null;
    constructor(opts) {
        this.opts = opts;
    }
    /** Install the unlock session (UMK in memory). Pass null on lock. */
    setSession(session) {
        this.session = session;
    }
    get unlocked() {
        return this.session !== null;
    }
    async nextNonce() {
        return this.opts.getNonce ? this.opts.getNonce() : uuidv4();
    }
    async call(opts) {
        const bodyText = opts.body === undefined ? undefined : JSON.stringify(opts.body);
        const headers = {};
        const jwt = await this.opts.getJwt();
        headers['authorization'] = `Bearer ${jwt}`;
        if (opts.umkProof === true) {
            const session = this.session;
            if (session === null) {
                throw new ApiError(401, 'umk_proof_invalid', 'vault is locked');
            }
            const nonce = await this.nextNonce();
            Object.assign(headers, await umkProofHeaders({
                session,
                method: opts.method,
                path: opts.path,
                nonce,
                bodyText: bodyText ?? '',
            }));
        }
        const res = await this.opts.transport.request({
            method: opts.method,
            path: opts.path,
            bodyText,
            headers,
        });
        if (res.status >= 400) {
            throw ApiError.from(res);
        }
        return opts.schema.parse(res.json);
    }
    // 1
    enroll(body) {
        return this.call({ method: 'POST', path: '/v1/enroll', body, schema: S.zEnrollResponse });
    }
    // 2
    ackRecoveryCode() {
        return this.call({
            method: 'POST',
            path: '/v1/enroll/recovery-code/ack',
            schema: S.zRecoveryAckResponse,
        });
    }
    // 3
    me() {
        return this.call({ method: 'GET', path: '/v1/me', schema: S.zMe });
    }
    // 4
    getUnlockEnvelopes() {
        return this.call({
            method: 'GET',
            path: '/v1/unlock/envelopes',
            schema: S.zUnlockEnvelopesResponse,
        });
    }
    // 5
    recordUnlockAttempt(body) {
        return this.call({
            method: 'POST',
            path: '/v1/unlock/attempt',
            body,
            schema: S.zUnlockAttemptResponse,
        });
    }
    // 6
    recoveryStart() {
        return this.call({
            method: 'POST',
            path: '/v1/recovery/start',
            schema: S.zRecoveryStartResponse,
        });
    }
    // 7
    recoveryComplete(body) {
        return this.call({
            method: 'POST',
            path: '/v1/recovery/complete',
            body,
            schema: S.zRecoveryCompleteResponse,
        });
    }
    // 8
    rotatePassphrase(body) {
        return this.call({
            method: 'POST',
            path: '/v1/passphrase/rotate',
            body,
            umkProof: true,
            schema: S.zOk,
        });
    }
    // 9
    listVaults() {
        return this.call({ method: 'GET', path: '/v1/vaults', schema: S.zVaultList });
    }
    // 10
    createVault(body) {
        return this.call({
            method: 'POST',
            path: '/v1/vaults',
            body,
            umkProof: true,
            schema: S.zVaultResponse,
        });
    }
    // 11
    updateVault(id, body) {
        return this.call({
            method: 'PATCH',
            path: `/v1/vaults/${encodeURIComponent(id)}`,
            body,
            umkProof: true,
            schema: S.zVaultResponse,
        });
    }
    // 12
    deleteVault(id) {
        return this.call({
            method: 'DELETE',
            path: `/v1/vaults/${encodeURIComponent(id)}`,
            umkProof: true,
            schema: S.zOk,
        });
    }
    // 13
    listItems(params) {
        const q = new URLSearchParams({ vault_id: params.vaultId });
        if (params.since !== undefined) {
            q.set('since', params.since);
        }
        return this.call({
            method: 'GET',
            path: `/v1/items?${q.toString()}`,
            umkProof: true,
            schema: S.zItemListResponse,
        });
    }
    // 14
    createItem(body) {
        return this.call({
            method: 'POST',
            path: '/v1/items',
            body,
            umkProof: true,
            schema: S.zItemResponse,
        });
    }
    // 15
    getItem(id) {
        return this.call({
            method: 'GET',
            path: `/v1/items/${encodeURIComponent(id)}`,
            umkProof: true,
            schema: S.zItemResponse,
        });
    }
    // 16
    updateItem(id, body) {
        return this.call({
            method: 'PATCH',
            path: `/v1/items/${encodeURIComponent(id)}`,
            body,
            umkProof: true,
            schema: S.zItemResponse,
        });
    }
    // 17
    deleteItem(id) {
        return this.call({
            method: 'DELETE',
            path: `/v1/items/${encodeURIComponent(id)}`,
            umkProof: true,
            schema: S.zOk,
        });
    }
    // 18
    lookupItems(domainHash) {
        const q = new URLSearchParams({ domain_hash: domainHash });
        return this.call({
            method: 'GET',
            path: `/v1/items/lookup?${q.toString()}`,
            umkProof: true,
            schema: S.zItemLookupResponse,
        });
    }
    // 19
    listDevices() {
        return this.call({ method: 'GET', path: '/v1/devices', schema: S.zDeviceList });
    }
    // 20
    pairInit(body) {
        return this.call({
            method: 'POST',
            path: '/v1/devices/pair/init',
            body,
            schema: S.zPairInitResponse,
        });
    }
    // 21
    pairPending() {
        return this.call({
            method: 'GET',
            path: '/v1/devices/pair/pending',
            umkProof: true,
            schema: S.zPairPendingResponse,
        });
    }
    // 22
    pairApprove(token, body) {
        return this.call({
            method: 'POST',
            path: `/v1/devices/pair/${encodeURIComponent(token)}/approve`,
            body,
            umkProof: true,
            schema: S.zPairApproveResponse,
        });
    }
    // 23
    pairClaim(token) {
        return this.call({
            method: 'POST',
            path: `/v1/devices/pair/${encodeURIComponent(token)}/claim`,
            schema: S.zPairClaimResponse,
        });
    }
    // 24
    revokeDevice(id) {
        return this.call({
            method: 'DELETE',
            path: `/v1/devices/${encodeURIComponent(id)}`,
            umkProof: true,
            schema: S.zOk,
        });
    }
    // 25
    listAudit(since) {
        const path = since === undefined ? '/v1/audit' : `/v1/audit?since=${encodeURIComponent(since)}`;
        return this.call({ method: 'GET', path, schema: S.zAuditListResponse });
    }
    // 26
    deleteAccount(body) {
        return this.call({
            method: 'DELETE',
            path: '/v1/account',
            body,
            umkProof: true,
            schema: S.zOk,
        });
    }
    // 27 — begin vault wipe (lost passphrase AND recovery code). jwt only; no unlock needed.
    wipeStart() {
        return this.call({ method: 'POST', path: '/v1/wipe/start', schema: S.zWipeStartResponse });
    }
    // 28 — confirm the wipe from the emailed link page. jwt + wipe-token, typed `WIPE`; NO umk-proof.
    wipeConfirm(body) {
        return this.call({ method: 'POST', path: '/v1/wipe/confirm', body, schema: S.zOk });
    }
    // 29 — cancel an outstanding wipe, or restore a soft-deleted account inside the 72h window
    // (all-device cancel). jwt only; no body (the live session is the auth).
    wipeCancel() {
        return this.call({ method: 'POST', path: '/v1/wipe/cancel', schema: S.zOk });
    }
}
//# sourceMappingURL=client.js.map